import { makeAutoObservable } from "mobx";
import apiv2 from "../apiv2";
import organizationsStore from "./organizationsStore";

class AppointmentStore {
  appointments = [];
  appointment = null;
  copyAppointment = null;
  medicalFile = null;
  medicalFiles = [];
  patientId = null;
  oldPatientId = null;
  folder = null;
  isEndAppointmentsLoading = false;
  isMedicalFileLoading = true;
  loadingAppointment = false;
  isDisplayFolders = true;

  constructor() {
    makeAutoObservable(this);
  }

  changeCopyAppointment = value => {
    this.copyAppointment = value;
  };

  setLoadingAppointment = value => {
    this.loadingAppointment = value;
  };

  setFolder = value => {
    this.folder = value;
  };

  changeIsEndAppointmentsLoading = value => {
    this.isEndAppointmentsLoading = value;
  };

  changeIsMedicalFileLoading = value => {
    this.isMedicalFileLoading = value;
  };

  changeIsDisplayFolders = value => {
    this.isDisplayFolders = value;
  };

  setAppointment(appointment) {
    this.appointment = appointment;
  }

  setMedicalFile(medicalFile) {
    this.medicalFile = medicalFile;
    this.setPatientId(medicalFile?.patient?.id);
    this.getAppointments();
    this.isMedicalFileLoading = false;
  }

  setPatientId(patientId) {
    this.patientId = patientId;
  }

  setAppointments(appointments) {
    this.appointments = appointments;
  }

  async getAppointmentsNextToEvaluate(id) {
    try {
      this.setLoadingAppointment(true);

      if (id) {
        this.setAppointment(await apiv2.appointments.getAppointmentById(id));
      } else {
        this.setAppointment(await apiv2.appointments.getAppointmentsNextToEvaluate());
      }
    } catch (e) {
      console.log(e);
    } finally {
      this.setLoadingAppointment(false);
    }

    return this.appointment;
  }

  async getAppointmentById(id, noLoader) {
    try {
      if (!noLoader) this.setLoadingAppointment(true);
      this.appointment = await apiv2.appointments.getAppointmentById(id);
    } catch (e) {
      console.log(e);
    } finally {
      this.setLoadingAppointment(false);
    }

    return this.appointment;
  }

  async getAppointments(isChange) {
    try {
      if (
        (typeof this.patientId !== "number" || this.oldPatientId === this.patientId) &&
        !isChange
      ) {
        this.isEndAppointmentsLoading = true;
        return;
      }
      this.isEndAppointmentsLoading = false;
      const result = await apiv2.appointments.getAppointmentsByPatientId(
        this.patientId,
        this.isDisplayFolders
      );
      this.oldPatientId = this.patientId;
      this.setAppointments(result.map(obj => ({ ...obj.data, type: obj.type })));
    } catch (e) {
      console.log(e);
    } finally {
      this.isEndAppointmentsLoading = true;
    }
  }

  async getMedicalFile(medicalFileId) {
    try {
      this.isMedicalFileLoading = true;
      if (!this.medicalFile || this.medicalFile.id !== medicalFileId) {
        const result = await apiv2.medicalFiles.getById(medicalFileId);
        this.setMedicalFile(result);
      } else {
        this.getAppointments();
      }
    } catch (e) {
      console.log(e);
    } finally {
      this.isMedicalFileLoading = false;
    }
  }

  async getMedicalFiles() {
    try {
      if (
        (typeof this.patientId !== "number" || this.oldPatientId === this.patientId) &&
        this.medicalFiles.length
      ) {
        return;
      }
      const response = await apiv2.medicalFilesV2.get({ patientId: this.patientId });
      this.medicalFiles = response;
      this.oldPatientId = this.patientId;
    } catch (e) {
      console.log(e);
    }
  }

  async getMedicalFilesForOrganizations() {
    await this.getMedicalFiles();
    return this.medicalFiles.filter(medicalFile => {
      return (
        organizationsStore
          .getDoctorOrganizations()
          .findIndex(organization => organization.id === medicalFile.organization.id) >= 0
      );
    });
  }

  getOpeningCheckupAppointments() {
    if (!this.isDisplayFolders) {
      const checkupAppointments = this.appointments.filter(
        appointment => appointment.folder && appointment.folder.name.startsWith("Чекап")
      );
      let counterOpeningCheckUp = 0;
      let counterClosingCheckUp = 0;
      return checkupAppointments.reduce((acc, appointment) => {
        appointment.layout.opening_check_up && counterOpeningCheckUp++;
        appointment.layout.closing_check_up && counterClosingCheckUp++;
        if (counterOpeningCheckUp !== counterClosingCheckUp || !appointment.date_finished) {
          acc.push(appointment);
        }
        return acc;
      }, []);
    }
    const checkupAppointments = this.appointments.filter(
      appointment => appointment.type === "folder" && appointment.name.startsWith("Чекап")
    );
    return checkupAppointments.reduce((acc, current) => {
      let counterOpeningCheckUp = 0;
      let counterClosingCheckUp = 0;
      current.appointments.forEach(item => {
        item.layout.opening_check_up && counterOpeningCheckUp++;
        item.layout.closing_check_up && counterClosingCheckUp++;
      });
      const isFinishedAppointmentClosingCheckUp = current.appointments.every(
        appointment => appointment.date_finished
      );
      if (counterOpeningCheckUp !== counterClosingCheckUp || !isFinishedAppointmentClosingCheckUp) {
        acc.push(current);
      }
      return acc;
    }, []);
  }

  get appointmentTickets() {
    return this.appointment?.stat_tickets || [];
  }

  get appointmentHasRedeemedStatTickets() {
    return this.appointmentTickets.some(ticket => ticket.is_redeemed);
  }
}

export default new AppointmentStore();
